import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, Users, Briefcase, Inbox, AlertCircle, Sparkles, Eye, ArrowRight } from "lucide-react";

export default function PlatformDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const { impersonate, user } = useAuth();
  const navigate = useNavigate();

  const viewAs = async (ownerId) => {
    await impersonate(ownerId);
    navigate("/dashboard");
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/platform/stats");
      setStats(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load platform stats");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="pgm-callout" data-testid="platform-error">{error}</div>;
  if (!stats) return <div>Loading platform stats…</div>;

  const t = stats.totals;
  const tiles = [
    { label: "Shows", value: t.shows, icon: Building2, testid: "stat-shows" },
    { label: "Admins", value: t.admins, icon: Users, testid: "stat-admins" },
    { label: "Total guests", value: t.guests, icon: Sparkles, testid: "stat-guests" },
    { label: "Sponsors", value: t.sponsors, icon: Briefcase, testid: "stat-sponsors" },
    { label: "Pending requests", value: t.pending_access_requests, icon: Inbox, testid: "stat-pending" },
    { label: "Suspended admins", value: t.suspended_admins, icon: AlertCircle, testid: "stat-suspended" },
  ];

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch (e) { return iso.slice(0, 10); }
  };

  return (
    <div className="pgm-reveal">
      <div className="pgm-micro">◆ Platform overview</div>
      <h1 className="pgm-display" style={{ fontSize: 44, marginTop: 4, marginBottom: 4 }} data-testid="platform-welcome">Welcome back{user?.name ? `, ${(user.name || "").split(" ")[0]}` : ""}</h1>
      <p style={{ color: "var(--pgm-wine)", opacity: 0.75, marginBottom: 28 }}>
        Cross-show analytics, user accounts, and inbound access requests. You don't own a show — you run the platform.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14 }} className="pgm-reveal-stagger">
        {tiles.map(({ label, value, icon: Icon, testid }) => (
          <div key={label} className="pgm-stat" data-testid={testid}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div className="pgm-micro">{label}</div>
              <Icon size={16} color="var(--pgm-magenta)" style={{ opacity: 0.6 }} />
            </div>
            <div className="pgm-stat-value">{value ?? "—"}</div>
          </div>
        ))}
      </div>

      <div className="pgm-divider">◆</div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
        <section>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
            <h2 className="pgm-display" style={{ fontSize: 26 }}>All shows</h2>
            <Link to="/admin/shows" className="pgm-link">Manage all →</Link>
          </div>
          {stats.per_show.length === 0 ? (
            <div className="pgm-callout"><strong>No shows yet.</strong> Create your first admin from User Accounts.</div>
          ) : (
            <table className="pgm-table" data-testid="platform-shows-table">
              <thead><tr><th>Show</th><th>Owner</th><th>Tier</th><th>Guests</th><th>Promos</th><th>Last activity</th><th></th></tr></thead>
              <tbody>
                {stats.per_show.map(s => (
                  <tr key={s.show_id} data-testid={`platform-show-${s.show_id}`}>
                    <td>
                      <strong>{s.show_name}</strong>
                      {s.suspended && <span className="pgm-badge pgm-badge-orange" style={{ marginLeft: 8, fontSize: 9 }}>Suspended</span>}
                    </td>
                    <td style={{ fontSize: 12 }}>{s.owner_name}<br/><span style={{ opacity: 0.65 }}>{s.owner_email}</span></td>
                    <td><span className={`pgm-badge ${s.access_tier === "paid" ? "pgm-badge-olive" : "pgm-badge"}`}>{s.access_tier}</span></td>
                    <td>{s.guest_count}</td>
                    <td>{s.guests_with_promo}</td>
                    <td style={{ fontSize: 11, opacity: 0.8 }}>{fmtDate(s.last_activity)}</td>
                    <td>
                      {s.owner_id && !s.suspended && (
                        <button className="pgm-btn pgm-btn-olive" style={{ padding: "5px 10px", fontSize: 10 }} onClick={() => viewAs(s.owner_id)} data-testid={`platform-view-as-${s.show_id}`}>
                          <Eye size={11} /> View as
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <aside>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
            <h2 className="pgm-display" style={{ fontSize: 26 }}>Recent requests</h2>
            <Link to="/admin/access-requests" className="pgm-link">All →</Link>
          </div>
          {stats.recent_access_requests.length === 0 ? (
            <div className="pgm-callout" style={{ background: "var(--pgm-cream-light)" }}>
              <Inbox size={14} style={{ verticalAlign: "middle", marginRight: 8 }} />
              No requests yet. Share your landing page URL to start collecting them.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {stats.recent_access_requests.map(r => (
                <Link key={r.id} to="/admin/access-requests" className="pgm-card" style={{ textDecoration: "none", display: "block", borderLeftColor: r.status === "pending" ? "var(--pgm-orange)" : "var(--pgm-olive)" }} data-testid={`platform-req-${r.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <strong style={{ color: "var(--pgm-wine)" }}>{r.name}</strong>
                      <div style={{ fontSize: 11, opacity: 0.7 }}>{r.show_name} · {fmtDate(r.created_at)}</div>
                    </div>
                    <ArrowRight size={14} color="var(--pgm-magenta)" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
